import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Link, ColorTokens } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { get } from 'lodash';
import { ChevronRightIconGlyph } from '@shapeable/icons';
import { LinkButton, LinkButtonProps, useEntity, useEntityPathDeriver, useModal, VideoPlayer } from '@shapeable/ui';
import { isMailchimpModalLink, showMailchimpModal } from './mailchimp-modal';

const cls = classNames('link-entity-button-swap');

// -------- Types -------->

export type LinkEntityButtonProps = Classable & HasChildren & Omit<LinkButtonProps, 'href'> & { 
  entity: Link;
  showTypeAsSubLabel?: boolean;
  colors?: ColorTokens.TextBackgroundBorder & ColorTokens.Fill;
};

export const LinkEntityButtonDefaultProps: Omit<LinkEntityButtonProps, 'entity'> = {
  showTypeAsSubLabel: true,
};

// -------- Styles -------->

type ContainerProps = {
  _backgroundColor?: string;
  showBrightness?: boolean;
};

const CustomButtonStyles = breakpoints({
  base: css`
    max-width: fit-content;

    .shp--button {

      ${({ showBrightness }: ContainerProps) => showBrightness && css`
        filter: brightness(1.1);
      `}

      ${({ _backgroundColor, showBrightness }: ContainerProps) => _backgroundColor && css`
        background: ${theme.COLOR(_backgroundColor)};
        border: none;
      `}
      color: ${theme.COLOR('light')};
      border-radius: ${theme.UNIT(8)};
      font-size: 0.8em;
      font-weight: 600;
      padding: 12px 24px;
      transition: all 0.3s ease;
      
      &:hover {
        filter: brightness(1.1);
        transform: translateX(8px);
      }
      
      &:active {
        transform: translateY(0) translateX(8px);
      }
    }
  `,
});

const VideoStyles = breakpoints({
});

// -------- Components -------->

const My = {
  Container: styled(LinkButton)<ContainerProps>`${CustomButtonStyles}`,
  Video: styled(VideoPlayer)`${VideoStyles}`,
};

export const LinkEntityButton: Shapeable.FC<LinkEntityButtonProps> = (props) => {
  const { className, children, showTypeAsSubLabel, ...rest } = props;
  const entity = useEntity(props.entity);
  
  const getEntityPath = useEntityPathDeriver();
  const modal = useModal();

  const { color } = entity;

  const pagePath = entity.page && getEntityPath(entity.page);
  const fileUrl = get(entity, 'file.file.url');
  const url = get(entity, 'url');
  const videoUrl = get(entity, 'video.url');
  const isDownload = rest.isDownload || !!entity.isDownload || (!pagePath && !!fileUrl);
  const href = pagePath || fileUrl || url;

  const label = children || entity.label || entity.name;
  const type = get(entity, 'type.name');
  const subLabel = showTypeAsSubLabel && type;

  const colorValue = color?.value || 'primary';
  const showBrightness = color?.slug === 'brightness';

  const isVideoLink = !pagePath && !fileUrl && !!videoUrl;
  const isMailchimpLink = isMailchimpModalLink(entity, { pagePath, fileUrl, videoUrl });
  const isModalLink = isVideoLink || isMailchimpLink;

  const handleModalClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isVideoLink) {
      modal.show(
        <My.Video playing url={videoUrl} />,
        { color: '#000000', title: entity.label || entity.name },
      );
      return;
    }

    showMailchimpModal(modal);
  };

  return (
   <My.Container 
    {...rest} 
     href={isModalLink ? undefined : href} 
     onClick={isModalLink ? handleModalClick : undefined}
     linkProps={isModalLink ? { onClick: (e) => e.preventDefault() } : undefined}
     _backgroundColor={colorValue}
     showBrightness={showBrightness}
     iconSize={11}
     subLabel={subLabel} 
     isDownload={isDownload} 
     icon={ChevronRightIconGlyph}
     colors={{ fill: 'light' }}
     className={cls.name(className)}
   >
     {label}
   </My.Container>
  )
};

LinkEntityButton.defaultProps = LinkEntityButtonDefaultProps;
LinkEntityButton.cls = cls;